import {
  type MutableRefObject,
  startTransition,
  useCallback,
  useMemo,
  useReducer,
  useRef,
} from 'react';
import { AbortError } from '../errors';
import { useIsMounted } from '../hooks/useIsMounted';
import { useRefAutoUpdate, useRefUpdating } from '../hooks/useRefUpdating';
import { injectIntoInfiniteData2 } from './infinite';
import { to } from './to';
import type {
  InfiniteStore,
  UseInfiniteRequestItem,
  UseInfiniteRequestOpts,
  UseRequestOpts,
} from './types';

// Items request limit while polling
const DEFAULT_LIMIT = 25;

// Default key to use while polling
const ID = 'id' as const;

interface NextInfiniteActionsReducerState<_T> {
  fetching: boolean;
  pending: boolean;
  baseFetching: boolean;
  nextFetching: boolean;
  prevFetching: boolean;
}

function nextInfiniteActionsReducer<T>(
  state: NextInfiniteActionsReducerState<T>,
  {
    action,
    values,
  }:
    | { action: 'RESET'; values?: Partial<NextInfiniteActionsReducerState<T>> }
    | { action: 'SET'; values: Partial<NextInfiniteActionsReducerState<T>> },
) {
  if (action === 'SET' && values) {
    let changed = 0;

    if (!state) {
      state = {
        fetching: false,
        pending: false,
        baseFetching: false,
        nextFetching: false,
        prevFetching: false,
      };
      changed = -1;
    }

    for (const k in values) {
      const key = k as keyof NextInfiniteActionsReducerState<T>;
      if (state[key] !== values[key]) {
        state[key] = values[key];
        changed = changed === 0 ? 1 : changed;
      }
    }

    if (changed === 1) {
      return { ...state };
    }
  } else if (action === 'RESET') {
    if (
      state.fetching ||
      !state.pending ||
      state.baseFetching ||
      state.nextFetching ||
      state.prevFetching
    ) {
      state = {
        fetching: false,
        pending: true,
        baseFetching: false,
        nextFetching: false,
        prevFetching: false,
      };
    }
  }

  return state;
}

export function useNextInfiniteActions<
  const ItemType extends API.DbItem,
  const BodyType = void,
  const QueryType extends API.BasePageInput = API.WithPageInput<ItemType>,
  const MetaType = void,
  const FromSocketType = ItemType,
>(
  key: string,
  opts: UseInfiniteRequestOpts<ItemType, BodyType, QueryType, MetaType, FromSocketType>,
  initialOpts: UseRequestOpts<API.WithPage<ItemType[], MetaType>, QueryType>,
  knownIdsRef: MutableRefObject<Set<API.Id>>,
  requestFetcher,
  basePayload,
  storeBank: UseInfiniteRequestItem<ItemType, MetaType>,
  _storeGet: InfiniteStore<ItemType, MetaType>['get'],
  storeSet: InfiniteStore<ItemType, MetaType>['set'],
  _storeRemove: InfiniteStore<ItemType, MetaType>['remove'],
  _wsReadyRef: MutableRefObject<boolean>,
) {
  const _isMounted = useIsMounted();

  const bank = storeBank?.data || basePayload?.data;
  const bankRef = useRefUpdating(bank);

  const abortControllerRef = useRef<Record<string, AbortController>>({});

  // Next Page
  const nextCursorVal = bank?.pageInfo?.endCursor;
  const nextCursorIdVal = bank?.pageInfo?.endCursorId;
  const nextPage = useRef({
    cursor: nextCursorVal,
    cursorId: nextCursorIdVal,
    running: false,
    has: !!bank?.pageInfo?.hasNextPage,
  });

  useRefAutoUpdate(nextPage, 'cursor', nextCursorVal);
  useRefAutoUpdate(nextPage, 'cursorId', nextCursorIdVal);
  useRefAutoUpdate(nextPage, 'has', !!bank?.pageInfo?.hasNextPage);

  // Prev Page
  const prevCursorVal = bank?.pageInfo?.startCursor;
  const prevCursorIdVal = bank?.pageInfo?.startCursorId;
  const prevPage = useRef({
    cursor: prevCursorVal,
    cursorId: prevCursorIdVal,
    running: false,
    has: !!bank?.pageInfo?.hasPreviousPage,
  });

  useRefAutoUpdate(prevPage, 'cursor', prevCursorVal);
  useRefAutoUpdate(prevPage, 'cursorId', prevCursorIdVal);
  useRefAutoUpdate(prevPage, 'has', !!bank?.pageInfo?.hasPreviousPage);

  // Loading states
  const [statusReducer, statusDispatch] = useReducer(
    nextInfiniteActionsReducer<ItemType>,
    undefined,
    () => ({
      fetching: false,
      pending: false,
      baseFetching: false,
      nextFetching: false,
      prevFetching: false,
    }),
  );

  //
  const isInitDescending = initialOpts?.query?.order?.dir === 'DESC';
  const initPageCursor = bank?.pageInfo?.[isInitDescending ? 'startCursor' : 'endCursor'];

  // Actions

  const fetchPage = useCallback(
    async <PageDirection extends 'before' | 'after', CursorKey extends 'endCursor' | 'startCursor'>(
      pageDirection: PageDirection,
      pageCursorKey: CursorKey,
      fetchCursor: NonNullable<NonNullable<(typeof opts)['query']>['page']>[PageDirection],
      prevAbortControllerKey: string,
      clear?: boolean | 'stay-empty',
    ) => {
      const pageCursorIdKey = `${pageCursorKey}Id` as const;

      if (
        abortControllerRef.current[prevAbortControllerKey] &&
        !abortControllerRef.current[prevAbortControllerKey]?.signal?.aborted
      ) {
        abortControllerRef.current[prevAbortControllerKey].abort?.();
      }

      if (clear) {
        // Clear any other runs
        for (const abortControllerKey in abortControllerRef.current) {
          if (
            abortControllerRef.current[abortControllerKey] &&
            !abortControllerRef.current[abortControllerKey]?.signal?.aborted
          ) {
            abortControllerRef.current[abortControllerKey].abort?.();
          }
        }

        statusDispatch({
          action: 'SET',
          values: { baseFetching: clear !== 'stay-empty', pending: clear === 'stay-empty' },
        });
      }

      const nextAbortController = new AbortController();
      abortControllerRef.current[prevAbortControllerKey] = nextAbortController;

      const [error, json] =
        clear === 'stay-empty'
          ? [null, {}]
          : await to(
              requestFetcher?.run?.(undefined, {
                dontStoreError: true,
                dontAlterState: true,
                rawResult: true,
                throwOnRun: true,
                abortController: nextAbortController,
                query: {
                  // Reuse other parts of query (ie. where clauses and order)
                  ...initialOpts?.query,
                  page:
                    clear && !fetchCursor
                      ? // if clearing (refetch), just use the original query params
                        initialOpts?.query?.page
                      : // Don't reuse anything from page if polling though
                        {
                          limit: initialOpts?.query?.page?.limit || DEFAULT_LIMIT,
                          after: undefined, // unset these from queryRef
                          before: undefined,
                          [pageDirection]: fetchCursor, // and only set one of them
                        },
                },
              }),
            );

      if (clear && clear !== 'stay-empty') {
        statusDispatch({ action: 'SET', values: { baseFetching: false, pending: false } });
      }

      if (error) {
        if (error instanceof AbortError) {
          // do nothing;
          return;
        }

        throw error; // uncaught
      }

      if (!json) {
        // this shouldn't happen...
        throw new Error(`useNextInfiniteActions/fetchPage: requestFetcher.run returned ${json}`);
      }

      const hasData = json.data?.length;
      const pageInfo = json.pageInfo;

      // The server's pageInfo.hasNextPage always reports "more rows exist beyond
      // this page in the fetched direction". Route it to the list-level flag for
      // THIS direction: an older page ('before' / endCursor) updates hasNextPage,
      // a newer page ('after' / startCursor — i.e. fetchPreviousPage) updates
      // hasPreviousPage. This keeps bidirectional infinite scroll (#1830) from
      // clobbering the opposite direction's "has more" flag.
      const dirHasKey = pageCursorKey === 'startCursor' ? 'hasPreviousPage' : 'hasNextPage';
      const dirHas = !!json.pageInfo?.hasNextPage;

      if (hasData || clear || dirHas !== bankRef.current?.pageInfo?.[dirHasKey]) {
        startTransition(() => {
          storeSet(
            key,
            (state, original) => {
              const origDataSet = original || bankRef.current;
              const draft = state.data;

              if (clear) {
                if (draft.data?.length) {
                  draft.data.length = 0;
                }

                if (
                  draft.pageInfo.total ||
                  draft.pageInfo.hasNextPage ||
                  draft.pageInfo.hasPreviousPage
                ) {
                  draft.pageInfo.total = 0;
                  draft.pageInfo.hasPreviousPage = false;
                  draft.pageInfo.hasNextPage = false;
                }

                if (draft.total) {
                  draft.total = 0;
                }

                knownIdsRef.current?.clear?.();
              }

              if (hasData) {
                injectIntoInfiniteData2(
                  json.data,
                  clear ? null : origDataSet,
                  draft,
                  knownIdsRef,
                  opts?.cursorKey,
                  undefined,
                  pageDirection === 'after' ? 'end' : 'start',
                );
              }

              const dirHasChanged = dirHas !== draft.pageInfo[dirHasKey];
              const limitChanged = pageInfo?.limit && pageInfo?.limit !== draft.pageInfo.limit;
              const totalChanged = pageInfo?.total && pageInfo?.total !== draft.pageInfo.total;
              const cursorChanged =
                pageInfo?.[pageCursorKey] &&
                pageInfo?.[pageCursorKey] !== draft.pageInfo[pageCursorKey];
              const cursorIdChanged =
                pageInfo?.[pageCursorIdKey] &&
                pageInfo?.[pageCursorIdKey] !== draft.pageInfo[pageCursorIdKey];

              if (dirHasChanged) {
                (draft as API.WithPage<ItemType[], void>).pageInfo[dirHasKey] = dirHas;
              }

              if (limitChanged) {
                draft.pageInfo.limit = pageInfo?.limit;
              }

              if (totalChanged) {
                draft.pageInfo.total = pageInfo?.total;
              }

              if (cursorChanged) {
                (draft as API.WithPage<ItemType[], void>).pageInfo[pageCursorKey] =
                  pageInfo?.[pageCursorKey];
              }

              if (cursorIdChanged) {
                (draft as API.WithPage<ItemType[], void>).pageInfo[pageCursorIdKey] =
                  pageInfo?.[pageCursorIdKey];
              }

              if (pageCursorKey === 'startCursor') {
                prevPage.current.has = dirHas;
              } else {
                nextPage.current.has = dirHas;
              }

              // draft.meta = json.meta || draft.meta;
            },
            // { resetTimeCached: true, key: curKey, notify: isMounted() },
          );
        });
      }

      return [pageInfo?.[pageCursorKey], pageInfo?.[pageCursorIdKey], json] as const;
    },
    [storeSet, key, requestFetcher, opts, initialOpts],
  );

  const fetchNextPage = useCallback(async () => {
    if (nextPage.current.running || !nextPage.current.has) {
      return;
    }

    nextPage.current.running = true;

    try {
      statusDispatch({ action: 'SET', values: { nextFetching: true } });

      const [fetchedCursor, fetchedCursorId] = await fetchPage(
        isInitDescending ? 'before' : 'after',
        'endCursor',
        initialOpts?.query?.order?.by !== ID
          ? [nextPage.current.cursor, nextPage.current.cursorId]
          : nextPage.current.cursor && nextPage.current.cursorId
            ? [nextPage.current.cursor, nextPage.current.cursorId]
            : initPageCursor,
        'next',
      );

      nextPage.current.cursor = fetchedCursor as any;
      nextPage.current.cursorId = fetchedCursorId as any;

      nextPage.current.running = false;
      statusDispatch({ action: 'SET', values: { nextFetching: false } });
    } catch (error) {
      nextPage.current.running = false;
      statusDispatch({ action: 'SET', values: { nextFetching: false } });
      throw error;
    }
  }, [fetchPage]);

  const fetchPreviousPage = useCallback(async () => {
    if (prevPage.current.running || !prevPage.current.has) {
      return;
    }

    prevPage.current.running = true;

    try {
      statusDispatch({ action: 'SET', values: { prevFetching: true } });

      const [fetchedCursor, fetchedCursorId] = await fetchPage(
        isInitDescending ? 'after' : 'before',
        'startCursor',
        // Mirror fetchNextPage: for an id-ordered list send the keyset TUPLE
        // [value, id] so the server pages by `(col, id) <op> (value, id)` and
        // actually advances past the newest loaded row. Passing the bare scalar
        // made a DESC list re-request already-loaded rows (a no-op), which broke
        // loading newer messages when scrolling down from a search anchor (#1830).
        initialOpts?.query?.order?.by !== ID
          ? [prevPage.current.cursor, prevPage.current.cursorId]
          : prevPage.current.cursor && prevPage.current.cursorId
            ? [prevPage.current.cursor, prevPage.current.cursorId]
            : initPageCursor,
        'prev',
      );

      prevPage.current.cursor = fetchedCursor as any;
      prevPage.current.cursorId = fetchedCursorId as any;

      prevPage.current.running = false;
      statusDispatch({ action: 'SET', values: { prevFetching: false } });
    } catch (error) {
      prevPage.current.running = false;
      statusDispatch({ action: 'SET', values: { prevFetching: false } });
      throw error;
    }
  }, [fetchPage]);

  const refetch = useCallback(async () => {
    const fetchPageResult = await fetchPage('after', 'endCursor', undefined, 'init', true);

    if (!fetchPageResult) {
      return;
    }

    const [_fetchedCursor, _fetchedCursorId, json] = fetchPageResult;

    const nextCursorVal = json?.pageInfo?.endCursor;
    const nextCursorIdVal = json?.pageInfo?.endCursorId;
    nextPage.current.cursor = nextCursorVal;
    nextPage.current.cursorId = nextCursorIdVal;
    nextPage.current.has = !!json?.pageInfo?.hasNextPage;

    const prevCursorVal = json?.pageInfo?.startCursor;
    const prevCursorIdVal = json?.pageInfo?.startCursorId;
    prevPage.current.cursor = prevCursorVal;
    prevPage.current.cursorId = prevCursorIdVal;
    prevPage.current.has = !!json?.pageInfo.hasPreviousPage;

    prevPage.current.running = false;
    nextPage.current.running = false;

    // The clear+inject in fetchPage only propagates the fetched direction's flag
    // (hasNextPage). An anchored "around" window (#1830) also returns a real
    // hasPreviousPage (newer rows exist beyond the anchor), so write BOTH flags
    // through to the store — otherwise the "load newer" affordance never appears
    // because DMReceiptList reads hasPreviousPage off the store bank's pageInfo.
    // Reuse the flags already read into the refs above rather than re-reading json.
    const nextHas = nextPage.current.has;
    const prevHas = prevPage.current.has;
    startTransition(() => {
      storeSet(key, (state) => {
        const draft = state.data;
        if (!draft?.pageInfo) {
          return;
        }
        if (draft.pageInfo.hasNextPage !== nextHas) {
          draft.pageInfo.hasNextPage = nextHas;
        }
        if (draft.pageInfo.hasPreviousPage !== prevHas) {
          draft.pageInfo.hasPreviousPage = prevHas;
        }
      });
    });

    return json;
  }, [fetchPage, key, storeSet]);

  const reset = useCallback(() => {
    statusDispatch({ action: 'RESET' });
    fetchPage('after', 'endCursor', undefined, 'init', 'stay-empty');
  }, []);

  const renderPayload = useCallback(
    (func: (items: API.WithPage<ItemType[], MetaType>) => any) => {
      if (storeBank?.data) {
        return func(storeBank.data);
      }
      if (basePayload?.data) {
        return func(basePayload?.data);
      }
      return null;
    },
    [basePayload, storeBank],
  );

  return useMemo(
    () => [statusReducer, fetchNextPage, fetchPreviousPage, refetch, renderPayload, reset] as const,
    [statusReducer, fetchNextPage, fetchPreviousPage, refetch, renderPayload, reset],
  );
}
